/**
 * How a page tells the topbar what it is.
 *
 * `.docs/08-web-foundation.md` §Design decisions: "The `PageTitle` slot pattern
 * replaces `data-title`/`<template id="topbar-actions">`." In the mockups a page
 * put its title in an attribute on `<main>` and its buttons in a `<template>` that
 * `shell.js` cloned into the topbar. Here a page renders one element:
 *
 * ```tsx
 * <PageTitle
 *   eyebrow="August 2026"
 *   title="Dashboard"
 *   sub="Casaturca · results since 4 March 2026"
 *   actions={<Link className="btn btn--sm" to="/app/billing">Money</Link>}
 * />
 * ```
 *
 * and the shell renders `.topbar` with them. No page writes topbar markup, which is
 * what keeps CONTRACT.md §2.1 true ("rendered by shell.js — do not hand-author").
 *
 * **Two mechanisms, and each for a reason.** The three strings go through context
 * state, because `document.title` and the `<h1>` are strings and a string is cheap
 * to compare. The `actions` node goes through a React portal into the topbar's own
 * `.topbar__actions` element, because a node is new on every render — put through
 * state it would set state during every render and never settle. A portal updates
 * in place, so a button can enable and disable itself without the shell knowing.
 *
 * **Unmounting clears, but only your own.** During a route change React can mount
 * the next page before it unmounts the last one, so a naive cleanup would clear the
 * title that has just been set. Each `PageTitle` holds a token and the cleanup is a
 * no-op unless the token still owns the slot.
 */

import {
  createContext,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

export type PageTitles = {
  readonly eyebrow?: string | undefined
  readonly title: string
  readonly sub?: string | undefined
}

type TopbarSlot = {
  readonly titles: PageTitles | null
  readonly claim: (token: string, titles: PageTitles | null) => void
  readonly actionsNode: HTMLElement | null
  readonly setActionsNode: (node: HTMLElement | null) => void
}

const TopbarContext = createContext<TopbarSlot | null>(null)

export function TopbarSlotProvider({ children }: { readonly children: ReactNode }) {
  const [owned, setOwned] = useState<{ token: string; titles: PageTitles } | null>(null)
  const [actionsNode, setActionsNode] = useState<HTMLElement | null>(null)

  const claim = useCallback((token: string, titles: PageTitles | null) => {
    setOwned((current) => {
      if (titles === null) return current?.token === token ? null : current
      return { token, titles }
    })
  }, [])

  const value = useMemo<TopbarSlot>(
    () => ({ titles: owned?.titles ?? null, claim, actionsNode, setActionsNode }),
    [owned, claim, actionsNode],
  )

  return <TopbarContext value={value}>{children}</TopbarContext>
}

function useTopbarSlot(): TopbarSlot {
  const slot = use(TopbarContext)
  if (slot === null) {
    throw new Error('<PageTitle> outside a portal layout. The layout routes provide the slot.')
  }
  return slot
}

/** What the topbar renders, and what `document.title` is built from. */
export function usePageTitles(): PageTitles | null {
  return useTopbarSlot().titles
}

/** The topbar's own hook: hands the element the actions portal renders into. */
export function useActionsSlot(): (node: HTMLElement | null) => void {
  return useTopbarSlot().setActionsNode
}

export type PageTitleProps = PageTitles & {
  /** Buttons and links for the right of the topbar. Portalled, never copied. */
  readonly actions?: ReactNode
}

export function PageTitle({ eyebrow, title, sub, actions }: PageTitleProps) {
  const { claim, actionsNode } = useTopbarSlot()
  const token = useId()

  useEffect(() => {
    claim(token, { eyebrow, title, sub })
    return () => {
      claim(token, null)
    }
  }, [claim, token, eyebrow, title, sub])

  if (actions === undefined || actionsNode === null) return null
  return createPortal(actions, actionsNode)
}
